import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Download, Image as ImageIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

export const ImageWatermarkTool: React.FC = () => {
  const { t } = useTranslation();
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [watermarkedImage, setWatermarkedImage] = useState<string | null>(null);
  const [watermarkText, setWatermarkText] = useState('WATERMARK');
  const [opacity, setOpacity] = useState(0.5);
  const [position, setPosition] = useState('bottom-right');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setOriginalImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const applyWatermark = () => {
    if (!originalImage || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Draw original image
      ctx.drawImage(img, 0, 0);
      
      // Set watermark style
      ctx.globalAlpha = opacity;
      ctx.fillStyle = 'white';
      ctx.strokeStyle = 'black';
      ctx.lineWidth = 2;
      ctx.font = `${Math.max(20, img.width / 20)}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      // Calculate position
      let x = img.width / 2;
      let y = img.height / 2;
      
      switch (position) {
        case 'top-left':
          x = img.width * 0.15;
          y = img.height * 0.15;
          break;
        case 'top-right':
          x = img.width * 0.85;
          y = img.height * 0.15;
          break;
        case 'bottom-left':
          x = img.width * 0.15;
          y = img.height * 0.85;
          break;
        case 'bottom-right':
          x = img.width * 0.85;
          y = img.height * 0.85;
          break;
        case 'center':
          x = img.width / 2;
          y = img.height / 2;
          break;
      }
      
      // Draw watermark with stroke for better visibility
      ctx.strokeText(watermarkText, x, y);
      ctx.fillText(watermarkText, x, y);
      
      // Convert to data URL
      const watermarkedDataUrl = canvas.toDataURL('image/png');
      setWatermarkedImage(watermarkedDataUrl);
    };
    
    img.src = originalImage;
  };

  const downloadImage = () => {
    if (watermarkedImage) {
      const link = document.createElement('a');
      link.download = 'watermarked-image.png';
      link.href = watermarkedImage;
      link.click();
    }
  };

  React.useEffect(() => {
    if (originalImage) {
      applyWatermark();
    }
  }, [originalImage, watermarkText, opacity, position]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <ImageIcon className="w-5 h-5" />
            <span>{t('tools.imageWatermark.name')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                className="w-full"
              >
                <Upload className="w-4 h-4 mr-2" />
                {t('tools.imageWatermark.selectImage')}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label={t('tools.imageWatermark.watermarkText')}
                value={watermarkText}
                onChange={(e) => setWatermarkText(e.target.value)}
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('tools.imageWatermark.position')}
                </label>
                <select
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  className="input"
                >
                  <option value="top-left">{t('tools.imageWatermark.positions.topLeft')}</option>
                  <option value="top-right">{t('tools.imageWatermark.positions.topRight')}</option>
                  <option value="center">{t('tools.imageWatermark.positions.center')}</option>
                  <option value="bottom-left">{t('tools.imageWatermark.positions.bottomLeft')}</option>
                  <option value="bottom-right">{t('tools.imageWatermark.positions.bottomRight')}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('tools.imageWatermark.opacity')} ({Math.round(opacity * 100)}%)
                </label>
                <input
                  type="range"
                  min="0.1"
                  max="1"
                  step="0.1"
                  value={opacity}
                  onChange={(e) => setOpacity(parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {originalImage && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('tools.imageWatermark.originalImage')}</CardTitle>
            </CardHeader>
            <CardContent>
              <img
                src={originalImage}
                alt="Original"
                className="w-full h-auto border border-gray-300 dark:border-gray-600 rounded"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{t('tools.imageWatermark.watermarkedImage')}</span>
                {watermarkedImage && (
                  <Button
                    onClick={downloadImage}
                    size="sm"
                    variant="outline"
                  >
                    <Download className="w-3 h-3 mr-1" />
                    {t('common.download')}
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {watermarkedImage ? (
                <img
                  src={watermarkedImage}
                  alt="Watermarked"
                  className="w-full h-auto border border-gray-300 dark:border-gray-600 rounded"
                />
              ) : (
                <div className="w-full h-64 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded flex items-center justify-center">
                  <span className="text-gray-500">{t('tools.imageWatermark.watermarkedImagePlaceholder')}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};
